import { useTranslation } from 'react-i18next';
import {
  AlgComponentType,
  UserRequirementType,
  User,
  UserReqRule,
  UserRequirement,
  UserRequirementInclusionType,
} from '@youshift/shared/types';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';
import { useState, useMemo } from 'react';
import { Tab } from '@headlessui/react';

import { RequirementTypeLegend } from '../../pages/Manager/IterationConfig/UserReqRules/InclusionTypeBadge';
import { RuleAttributes } from '../../pages/Manager/IterationConfig/UserReqRules/hooks/useClassifyRule';
import UserReqPriorityPerRule from './UserReqPriorityPerRule';
import UserReqPriorityPerUser from './UserReqPriorityPerUser';
import {
  getMinComponentTypeFromUserReq,
  getMaxComponentTypeFromUserReq,
  getReqValueFromUserReq,
  getConstraintCountByType,
} from './utils';
import InfoIcon from '../InfoIcon';

interface UserReqPriorityProps {
  allUserReqRules: Record<number, UserReqRule>;
  ruleAttributes: Record<number, RuleAttributes>;
  users: Record<number, User>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
}

enum ViewMode {
  RULES = 'rules',
  USERS = 'users',
}

const getRequirementTranslationKey = (
  reqType: UserRequirementType,
  isMin: boolean,
): string => {
  switch (reqType) {
    case UserRequirementType.DURATION:
      return isMin
        ? 'manager.smartass.algorithmComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_DURATION'
        : 'manager.smartass.algorithmComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_DURATION';
    case UserRequirementType.SLOTS:
      return isMin
        ? 'manager.smartass.algorithmComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_SLOTS'
        : 'manager.smartass.algorithmComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_SLOTS';
    case UserRequirementType.COUNTER_UNIT:
      return isMin
        ? 'manager.smartass.algorithmComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_COUNTER_UNIT'
        : 'manager.smartass.algorithmComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_COUNTER_UNIT';
    default:
      return '';
  }
};

const getUserReqUnitTranslationKey = (userReq: UserRequirement) => {
  switch (userReq.req_type) {
    case UserRequirementType.DURATION:
      return 'generic.duration';
    case UserRequirementType.SLOTS:
      return 'generic.slots';
    case UserRequirementType.COUNTER_UNIT:
      return 'generic.counterUnit';
    default:
      return '';
  }
};

export default function UserReqPriority({
  allUserReqRules,
  ruleAttributes,
  users,
  importanceValues,
  setImportanceValues,
}: UserReqPriorityProps) {
  const { t } = useTranslation();

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.RULES);

  const ruleConstraints = useMemo(
    () =>
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_SLOTS,
      ) +
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_SLOTS,
      ) +
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_DURATION,
      ) +
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_DURATION,
      ) +
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MIN_COUNTER_UNIT,
      ) +
      getConstraintCountByType(
        importanceValues,
        AlgComponentType.C_USER_SHIFT_REQUIREMENT_RULE_MAX_COUNTER_UNIT,
      ),
    [importanceValues],
  );

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-medium">
          {t('manager.smartass.algorithmSettingsModal.userReqPriority.title')}
        </h3>
        <InfoIcon
          title={t(
            'manager.smartass.algorithmSettingsModal.userReqPriority.title',
          )}
          content={
            <>
              <p className="pt-4">
                {t(
                  'manager.smartass.algorithmSettingsModal.userReqPriority.description',
                )}
              </p>
              <p className="mt-2 font-medium">
                {t('manager.smartass.algorithmSettingsModal.constraintCount', {
                  count: ruleConstraints,
                })}
              </p>
            </>
          }
          className="w-4 h-4"
          iconSize={6}
          size="2xl"
        />
      </div>
      <RequirementTypeLegend />

      <div className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-4">
        <button
          onClick={() => setViewMode(ViewMode.RULES)}
          className={`w-full rounded-lg py-2.5 text-sm font-medium leading-5 ${
            viewMode === ViewMode.RULES
              ? 'bg-white text-blue-700 shadow'
              : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
          }`}
        >
          {t('manager.smartass.algorithmSettingsModal.byUserReqRule')}
        </button>
        <button
          onClick={() => setViewMode(ViewMode.USERS)}
          className={`w-full rounded-lg py-2.5 text-sm font-medium leading-5 ${
            viewMode === ViewMode.USERS
              ? 'bg-white text-blue-700 shadow'
              : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
          }`}
        >
          {t('manager.smartass.algorithmSettingsModal.byUser')}
        </button>
      </div>
      {viewMode === ViewMode.RULES ? (
        <UserReqPriorityPerRule
          allUserReqRules={allUserReqRules}
          ruleAttributes={ruleAttributes}
          users={users}
          importanceValues={importanceValues}
          setImportanceValues={setImportanceValues}
          getRequirementTranslationKey={getRequirementTranslationKey}
          getUserReqUnitTranslationKey={getUserReqUnitTranslationKey}
          getMinComponentType={getMinComponentTypeFromUserReq}
          getMaxComponentType={getMaxComponentTypeFromUserReq}
          getReqValue={getReqValueFromUserReq}
        />
      ) : (
        <UserReqPriorityPerUser
          allUserReqRules={allUserReqRules}
          ruleAttributes={ruleAttributes}
          users={users}
          importanceValues={importanceValues}
          setImportanceValues={setImportanceValues}
          getRequirementTranslationKey={getRequirementTranslationKey}
          getUserReqUnitTranslationKey={getUserReqUnitTranslationKey}
          getMinComponentType={getMinComponentTypeFromUserReq}
          getMaxComponentType={getMaxComponentTypeFromUserReq}
          getReqValue={getReqValueFromUserReq}
        />
      )}
    </div>
  );
}
